import React, { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { fetchOptions, fetchWithCSRFRetry, updateCSRFToken } from '../../services/csrf'

export function StaffLoginPage(): React.ReactElement {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      // Submit to Devise staff session
      const response = await fetchWithCSRFRetry('/admin/sign_in', {
        ...fetchOptions('POST'),
        body: JSON.stringify({
          staff: { email, password },
        }),
      })

      if (response.ok) {
        const data = await response.json().catch(() => ({}))
        if (data.csrf_token) {
          updateCSRFToken(data.csrf_token)
        }
        // Redirect to Control Center
        window.location.href = '/control_center'
      } else {
        const data = await response.json().catch(() => ({}))
        setError(data.error || 'Invalid email or password')
      }
    } catch {
      setError('An unexpected error occurred')
    }

    setIsSubmitting(false)
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
        {/* Circuit Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="circuit" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 0 10 L 10 10 M 10 0 L 10 10" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
                <circle cx="10" cy="10" r="1" fill="currentColor" className="text-primary" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#circuit)" />
          </svg>
        </div>

        {/* Accent Lines */}
        <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-primary via-primary/50 to-transparent" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="mb-8">
            {/* Logo */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-orange-600 rounded-xl flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 52.32 52.29">
                  <g>
                    <path fill="currentColor" d="M20.39,26.26h-4.01l1.29-4.6,2.71,4.6h0ZM34.94,38.17l-12.84-21.54c-1.54-2.5-5.05-2.76-8.51-2.76l.02.02h-.38l-7.36,24.28h7.19l1.7-6.11h9.04l1.98,3.37c1.54,2.5,5.69,2.76,9.16,2.76"/>
                    <path fill="currentColor" d="M44.16,20.87c0-3.89-3.15-7.04-7.04-7.04h-14.11c2.23.43,3.09,2.57,4.73,5.11h8.52c1.07,0,1.93.86,1.93,1.93s-.86,1.93-1.93,1.93h-6.31l7.67,12.63c1.29,2.09,4.13,2.64,7.03,2.76h2.28l-6.66-11.02c2.3-1.16,3.9-3.53,3.9-6.29"/>
                  </g>
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Control Center</h1>
                <p className="text-white/50 text-sm tracking-wide">AutoRevo Internal</p>
              </div>
            </div>

            <h2 className="text-3xl font-light leading-tight mb-4">
              Staff
              <span className="block text-primary font-bold">Administration</span>
            </h2>

            <p className="text-white/60 max-w-sm">
              Access dealer management, impersonation tools, and system administration.
            </p>
          </div>

          {/* Access Level Info */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
            <h3 className="text-sm font-medium text-white/80 mb-4 uppercase tracking-wider">Access Levels</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="text-white/70">Super Staff &mdash; Full system access</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-400" />
                <span className="text-white/70">Staff &mdash; Support access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900 to-transparent" />
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-orange-600 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
                </svg>
              </div>
            </div>
            <h1 className="text-xl font-bold text-white">Control Center</h1>
          </div>

          {/* Form Card */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 lg:p-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Staff Sign In</h2>
              <p className="text-slate-400">Use your @autorevo.com credentials</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  pattern=".+@autorevo\.com"
                  title="Must be an @autorevo.com email"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors outline-none text-white placeholder:text-slate-500"
                  placeholder="you@autorevo.com"
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors outline-none text-white placeholder:text-slate-500"
                  placeholder="Enter your password"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-primary to-orange-600 hover:from-primary/90 hover:to-orange-500 disabled:from-slate-600 disabled:to-slate-600 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:transform-none shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                    </svg>
                    Sign In
                  </>
                )}
              </button>
            </form>

            {/* Google OAuth Notice */}
            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-center text-sm text-slate-500">
                Google OAuth coming soon
              </p>
            </div>
          </div>

          {/* Dealer Login Link */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              Dealer login &rarr;
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
